import type { Sheet, SheetCell } from './types';

/** Белая заливка визуально неотличима от пустой ячейки — содержимым её не считаем. */
const BLANK_BACKGROUNDS = new Set(['#FFFFFF', '#FFFFFE']);

/**
 * Excel пишет записи и для пустых, но отформатированных ячеек, поэтому лист обычно тянется
 * далеко вправо и вниз за пределы данных. Такой хвост даёт пустую зону в просмотре — обрезаем.
 * Цветную заливку считаем содержимым: ей рисуют разделители и подложки, без неё лист «поедет».
 */
function hasContent(cell?: SheetCell): boolean {
  if (!cell) {
    return false;
  }
  if (cell.text || cell.dataBar || cell.icon) {
    return true;
  }
  const borders = cell.style?.borders;
  if (borders && Object.values(borders).some(Boolean)) {
    return true;
  }
  const background = cell.style?.background?.toUpperCase();
  return Boolean(background && !BLANK_BACKGROUNDS.has(background));
}

export function trimSheet(sheet: Sheet): Sheet {
  let lastRow = -1;
  let lastColumn = -1;

  sheet.rows.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      if (!hasContent(cell)) {
        return;
      }
      lastRow = Math.max(lastRow, rowIndex);
      lastColumn = Math.max(lastColumn, columnIndex);
    });
  });

  // объединение, начатое в непустой ячейке, тянется дальше неё — иначе обрежем его хвост
  sheet.merges.forEach((merge) => {
    if (!hasContent(sheet.rows[merge.rowFrom]?.[merge.colFrom])) {
      return;
    }
    lastRow = Math.max(lastRow, merge.rowTo);
    lastColumn = Math.max(lastColumn, merge.colTo);
  });

  sheet.images.forEach((image) => {
    lastRow = Math.max(lastRow, image.to?.row ?? image.from.row);
    lastColumn = Math.max(lastColumn, image.to?.col ?? image.from.col);
  });

  if (lastRow < 0 || lastColumn < 0) {
    return { ...sheet, rows: [], merges: [] };
  }

  return {
    ...sheet,
    rows: sheet.rows.slice(0, lastRow + 1).map((row) => row.slice(0, lastColumn + 1)),
    merges: sheet.merges.filter((merge) => merge.rowFrom <= lastRow && merge.colFrom <= lastColumn),
  };
}
